import classNames from 'classnames';
import React, { FunctionComponent } from 'react';
import { BackIcon } from '@trello/nachos/icons/back';
import { InformationIcon } from '@trello/nachos/icons/information';
import { CloseIcon } from '@trello/nachos/icons/close';
import { IconButton } from 'app/common/components/controls/button/button';
import { forNamespace } from 'app/common/lib/util/i18n';
import { Noun, Verb } from '@trello/analytics';
import { track } from '../Analytics';

import styles from './OnboardingDialog.less';

const WaveImageUrl = require('resources/images/onboarding-banner/Wave.svg');
const TacoImageUrl = require('resources/images/onboarding-banner/Taco.svg');
const ErrorImageUrl = require('resources/images/onboarding-banner/error-and-cloud.svg');

const format = forNamespace(['aa onboarding', 'dialog'], {
  shouldEscapeStrings: false,
});
const analyticsSource = 'onboardingDialog';

interface DialogProps {
  title: React.ReactNode;
  subtitle?: string;
  helpLink?: string;
  closeDialog?: () => void;
  onClickGoBack?: () => void;
  className?: string;
  showError?: boolean;
}

export const preloadOnboardingImages = () => {
  new Image().src = WaveImageUrl;
  new Image().src = TacoImageUrl;
};

export const OnboardingDialog: FunctionComponent<DialogProps> = ({
  title,
  subtitle,
  helpLink,
  children,
  closeDialog,
  onClickGoBack,
  className,
  showError,
}) => (
  <div className={styles.dialog}>
    {onClickGoBack && (
      <div className={styles.backButton}>
        <a role="link" onClick={() => onClickGoBack()}>
          <BackIcon size="small" color="quiet" />
          {format('back')}
        </a>
      </div>
    )}
    <div className={styles.controls}>
      {helpLink && (
        <IconButton
          icon={<InformationIcon color="dark" size="large" />}
          className={styles.icon}
          onClick={() => {
            track({
              verb: Verb.OPENS,
              noun: Noun.HELP_DOC_LINK,
              method: 'by clicking overlay icon',
              context: {
                url: helpLink,
              },
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianOnboardingHelpDocButton',
            });
            window.open(helpLink, '_blank');
          }}
        />
      )}
      {closeDialog && (
        <IconButton
          icon={<CloseIcon color="dark" size="large" />}
          className={styles.icon}
          onClick={() => {
            track({
              verb: Verb.DISMISSES,
              noun: Noun.ONBOARDING_MENU,
              method: 'by clicking dismiss button',
              source: analyticsSource,
              action: 'clicked',
              actionSubject: 'button',
              actionSubjectId: 'atlassianAccountOnboardingOverlayCloseButton',
            });
            closeDialog();
          }}
        />
      )}
    </div>
    <div className={classNames(styles.dialogContent, className)}>
      <header className={styles.header}>
        <h2 className={styles.dialogTitle}>
          {title}
          {subtitle ? <div>{subtitle}</div> : undefined}
        </h2>
      </header>
      <div className={styles.dialogBody}>{children}</div>
    </div>
    <img alt="" src={WaveImageUrl} className={styles.dialogWave} />
    {showError && (
      <img alt="" src={ErrorImageUrl} className={styles.dialogError} />
    )}
    {!showError && (
      <img alt="" src={TacoImageUrl} className={styles.dialogTaco} />
    )}
  </div>
);
